import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  IdentificationIcon,
  MapIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";

function AddJewellerCustomer() {
  const { id } = useParams();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    dob: "",
    anniversary: "",
    customer_type: "regular",
    aadhar_number: "",
    permanent_address: "",
    residence_address: "",
    assigned_staff_id: "",
  });
  const [kycFiles, setKycFiles] = useState([]);
  const [existingKyc, setExistingKyc] = useState([]);
  const [removeKyc, setRemoveKyc] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/staff")
      .then((res) => setStaffList(res.data))
      .catch((err) =>
        console.error("Staff API error:", err.response || err.message)
      );
  }, []);

  useEffect(() => {
    if (id) {
      setLoading(true);
      api
        .get(`/jeweller-customers/${id}`)
        .then((res) => {
          const data = res.data;
          let kycDocs = [];
          if (Array.isArray(data.kyc_documents)) {
            kycDocs = data.kyc_documents;
          } else if (typeof data.kyc_documents === "string") {
            try {
              kycDocs = JSON.parse(data.kyc_documents);
            } catch {
              kycDocs = [];
            }
          }
          setForm({
            full_name: data.full_name || "",
            email: data.email || "",
            mobile: data.mobile || "",
            dob: data.dob || "",
            anniversary: data.anniversary || "",
            customer_type: data.customer_type || "regular",
            aadhar_number: data.aadhar_number || "",
            permanent_address: data.permanent_address || "",
            residence_address: data.residence_address || "",
            assigned_staff_id: data.assigned_staff_id || "",
          });
          setExistingKyc(kycDocs);
        })
        .catch((err) => {
          console.error("Fetch customer error:", err.response || err.message);
          alert("Failed to fetch customer");
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileChange = (e) => setKycFiles(Array.from(e.target.files));

  const toggleRemoveExisting = (path) => {
    setRemoveKyc((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(form).forEach((key) => data.append(key, form[key] ?? ""));
    kycFiles.forEach((file) => data.append("kyc_documents[]", file));
    removeKyc.forEach((p) => data.append("remove_kyc[]", p));

    try {
      if (id) {
        data.append("_method", "PUT");
        await api.post(`/jeweller-customers/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Customer updated successfully!");
      } else {
        await api.post("/jeweller-customers", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Customer added successfully!");
      }
      navigate("/jewellers/customers");
    } catch (err) {
      console.error("Save error:", err.response || err.message);
      if (err.response?.data?.errors) {
        const messages = Object.values(err.response.data.errors)
          .flat()
          .join("\n");
        alert(messages);
      } else {
        alert("Error saving customer");
      }
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="bg-gray-50 flex justify-center p-6">
      <div className="w-full bg-white rounded-2xl shadow-lg p-8 max-w-3xl">
        {/* Header */}
        <div className="flex items-center mb-6 gap-2">
          <UserGroupIcon className="h-6 w-6 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-800">
            {id ? "Edit Customer" : "Add Jeweller Customer"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <UserIcon className="h-4 w-4 text-purple-500" /> Full Name
              </label>
              <input type="text" name="full_name" placeholder="Enter full name" value={form.full_name} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400" required/>
            </div>

            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <EnvelopeIcon className="h-4 w-4 text-purple-500" /> Email
              </label>
              <input type="email" name="email" placeholder="Enter email" value={form.email} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400" required/>
            </div>
          </div>

          {/* Mobile & Aadhaar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <PhoneIcon className="h-4 w-4 text-purple-500" /> Mobile
              </label>
              <input type="text" name="mobile" placeholder="Enter mobile" value={form.mobile} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400" required/>
            </div>

            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <IdentificationIcon className="h-4 w-4 text-purple-500" /> Aadhaar Number
              </label>
              <input type="text" name="aadhar_number" placeholder="Enter Aadhaar" value={form.aadhar_number} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400"/>
            </div>
          </div>

          {/* DOB & Anniversary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <CalendarIcon className="h-4 w-4 text-purple-500" /> Date of Birth
              </label>
              <input type="date" name="dob" value={form.dob} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"/>
            </div>
            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <CalendarIcon className="h-4 w-4 text-purple-500" /> Anniversary
              </label>
              <input type="date" name="anniversary" value={form.anniversary} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"/>
            </div>
          </div>

          {/* Customer Type */}
          <div className="relative">
            <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
              <ClipboardDocumentListIcon className="h-4 w-4 text-purple-500" /> Customer Type
            </label>
            <select name="customer_type" value={form.customer_type} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white focus:ring-1 focus:ring-purple-500 focus:border-purple-500">
              <option value="regular">Regular</option>
              <option value="vip">VIP</option>
              <option value="walk-in">Walk-in</option>
              <option value="online">Online</option>
            </select>
          </div>

          {/* Addresses */}
          <div className="relative">
            <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
              <MapIcon className="h-4 w-4 text-purple-500" /> Permanent Address
            </label>
            <textarea name="permanent_address" placeholder="Enter permanent address" value={form.permanent_address} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-none placeholder-gray-400"></textarea>
          </div>

          <div className="relative">
            <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
              <MapIcon className="h-4 w-4 text-purple-500" /> Residence Address
            </label>
            <textarea name="residence_address" placeholder="Enter residence address" value={form.residence_address} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-none placeholder-gray-400"></textarea>
          </div>

          {/* Assign Staff */}
          <div className="relative">
            <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
              <UserGroupIcon className="h-4 w-4 text-purple-500" /> Assign Staff
            </label>
            <select name="assigned_staff_id" value={form.assigned_staff_id} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white focus:ring-1 focus:ring-purple-500 focus:border-purple-500">
              <option value="">Select Staff</option>
              {staffList.map((staff) => (
                <option key={staff.id} value={staff.user_id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          {/* KYC Documents */}
          <div className="relative">
            <label className="flex items-center gap-1 text-gray-500 text-sm mb-1">
              <DocumentIcon className="h-4 w-4 text-purple-500" /> KYC Documents
            </label>

            {Array.isArray(existingKyc) && existingKyc.length > 0 && (
              <div className="mb-2 space-y-1">
                <div className="font-medium text-gray-700">Existing documents:</div>
                {existingKyc.map((path) => (
                  <div key={path} className="flex items-center gap-3 text-sm">
                    <a href={`${process.env.REACT_APP_API_URL}/storage/${path}`} target="_blank" rel="noreferrer" className="text-purple-600 underline">
                      {path.split("/").pop()}
                    </a>
                    <label className="flex items-center gap-1 text-gray-600">
                      <input type="checkbox" checked={removeKyc.includes(path)} onChange={() => toggleRemoveExisting(path)}/>
                        {" "} Remove
                    </label>
                  </div>
                ))}
              </div>
            )}

            <input type="file" multiple onChange={handleFileChange} className="w-full text-gray-600"/>
            <div className="text-sm text-gray-500 mt-1">
              Upload new files to add to KYC.
            </div>
          </div>

          {/* Submit */}
          <button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-xl shadow-md transition-all duration-200">
            {id ? "Update Customer" : "Add Customer"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddJewellerCustomer;
